import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate, formatMoney, formatPercent } from '@/utils/format';

export interface AccountTransactionDetail {
  id: string;
  direction: 'in' | 'out';
  method: string;
  partyName: string;
  amountMinor: number;
  currency: string;
  transactionDate: string;
  reference: string | null;
  note?: string | null;
  isVoided: boolean;
  voidReason: string | null;
  funds?: Array<{ fundId: string | null; fundName: string | null; amountMinor: number }>;
}

export interface FinancialPositionTransactionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: AccountTransactionDetail | null;
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3">
    <dt className="text-body-sm text-fg-secondary">{label}</dt>
    <dd className="text-body-sm text-fg">{value}</dd>
  </div>
);

export const FinancialPositionTransactionDetailDialog = ({
  open,
  onOpenChange,
  transaction,
}: FinancialPositionTransactionDetailDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const funds = transaction?.funds ?? [];
  const signedAmount = transaction
    ? transaction.direction === 'out'
      ? -transaction.amountMinor
      : transaction.amountMinor
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('financialPosition.transactions.fundSplit.detailTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody contentClassName="flex flex-col gap-6">
          {transaction ? (
            <>
              <dl className="flex flex-col gap-3">
                <DetailRow
                  label={t('financialPosition.transactions.columns.date')}
                  value={formatDate(transaction.transactionDate)}
                />
                <DetailRow
                  label={t('financialPosition.transactions.columns.direction')}
                  value={t(`financialPosition.transactions.directions.${transaction.direction}`)}
                />
                <DetailRow
                  label={t('financialPosition.transactions.columns.method')}
                  value={t(`expenses.methods.${transaction.method}`)}
                />
                <DetailRow
                  label={t('financialPosition.transactions.columns.party')}
                  value={transaction.partyName}
                />
                <DetailRow
                  label={t('financialPosition.transactions.columns.amount')}
                  value={formatMoney(signedAmount, transaction.currency, {
                    parenthesiseNegative: true,
                  })}
                />
                <DetailRow
                  label={t('financialPosition.transactions.columns.reference')}
                  value={transaction.reference?.trim() || t('financialPosition.transactions.fundSplit.none')}
                />
                <DetailRow
                  label={t('financialPosition.transactions.note')}
                  value={transaction.note?.trim() || t('financialPosition.transactions.fundSplit.none')}
                />
                {transaction.isVoided ? (
                  <DetailRow
                    label={t('financialPosition.transactions.voidedLabel')}
                    value={transaction.voidReason?.trim() || t('financialPosition.transactions.fundSplit.none')}
                  />
                ) : null}
              </dl>
              {funds.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <p className="text-body-sm font-medium text-fg">
                    {t('financialPosition.transactions.fundSplit.title')}
                  </p>
                  <div className="overflow-hidden rounded-lg border border-border">
                    <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_5rem] gap-2 border-b border-border bg-surface-sunken px-3 py-2 text-caption font-medium text-fg-secondary">
                      <span>{t('financialPosition.transactions.fundSplit.fund')}</span>
                      <span className="text-right">{t('financialPosition.transactions.fundSplit.amount')}</span>
                      <span className="text-right">{t('financialPosition.transactions.fundSplit.share')}</span>
                    </div>
                    <div className="divide-y divide-border">
                      {funds.map((line, index) => (
                        <div
                          key={line.fundId ?? `untied-${index}`}
                          className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_5rem] gap-2 px-3 py-3 text-body-sm"
                        >
                          <span>
                            {line.fundName ?? t('financialPosition.transactions.fundSplit.notTied')}
                          </span>
                          <span className="text-right tabular-nums">
                            {formatMoney(line.amountMinor, transaction.currency)}
                          </span>
                          <span className="text-right tabular-nums text-fg-secondary">
                            {formatPercent(line.amountMinor / transaction.amountMinor)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </DialogBody>
        <DialogFooter className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
